import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  searchCities,
  type AshtakootaResult,
  type AspectHit,
  type BirthInput,
  type BirthTimeInput,
  type ChartResult,
  type CityEntry,
  type KootaScore,
  type Planet,
  type PlanetPosition,
} from '@astrally/shared';
import { computeAshtakoota, computeChart, computeSynastry, houseOf } from '@astrally/astro-engine';
import { deriveAstroSummary, useApp } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { InfoDot, ScreenHeader } from '../../components/common';
import { Term } from '../../components/Term';
import { localdb } from '../../lib/localdb';
import { AshtakootaTable, type DoshaFlags, type KootaRange } from './AshtakootaTable';
import { SynastryView, type HouseOverlay } from './SynastryView';

/**
 * Kundali Milan and Western synastry, side by side.
 *
 * This is the screen the product is positioned on: nowhere else tells you what
 * BOTH traditions say about the same two people and, more importantly, why
 * they can disagree without either being wrong. Everything here is computed
 * on-device by packages/astro-engine — `computeAshtakoota` and
 * `computeSynastry` — and no AI call fires unless the reader presses the
 * button that says it costs a message.
 *
 * PRIVACY. A second person's birth details are the most sensitive thing this
 * app ever holds, and they are not ours. Astrally never transmits another
 * person's birth data, never receives it from a connection's account (the
 * cloud only ever holds sign-level summaries, and never for someone else to
 * read), and stores what you type here in on-device IndexedDB with a visible
 * delete button. That constraint is also why a connection cannot be compared
 * automatically: the numbers below need real birth details, and the only
 * honest way to get someone's is to ask them.
 */

// ---------------------------------------------------------------------------
// Deterministic helpers
// ---------------------------------------------------------------------------

type KootaId = KootaScore['koota'];

interface MoonCandidate {
  rashi: number;
  nakshatra: number;
}

const NAK_SPAN = 360 / 27;

/** A nakshatra and a rashi are only a possible pair if their arcs overlap. */
function nakshatraTouchesRashi(nakshatra: number, rashi: number): boolean {
  const nStart = nakshatra * NAK_SPAN;
  const rStart = rashi * 30;
  return nStart < rStart + 30 && rStart < nStart + NAK_SPAN;
}

/**
 * Every Moon position this chart could actually have.
 *
 * With an exact time this is one entry. With a window the engine reports the
 * sign and the star as separate lists of possibilities, and they vary
 * independently — a Moon can hold its sign while crossing a nakshatra
 * boundary. Pairing the two lists by index (as the matching engine does for a
 * coarse score) would silently drop that case, so the combinations are crossed
 * and then filtered to the ones that are geometrically possible.
 */
function moonCandidates(chart: ChartResult): MoonCandidate[] {
  const summary = deriveAstroSummary(chart);
  const signs = summary.moonSiderealSigns.slice(0, 3);
  const naks = summary.moonNakshatras.slice(0, 4);
  if (signs.length === 0 || naks.length === 0) return [];

  const out: MoonCandidate[] = [];
  for (const rashi of signs) {
    for (const nakshatra of naks) {
      if (nakshatraTouchesRashi(nakshatra, rashi)) out.push({ rashi, nakshatra });
    }
  }
  // Defensive: if the overlap filter rejects everything (an engine change, a
  // corrupt cache) fall back to the index pairing rather than showing nothing.
  if (out.length === 0) {
    return signs.map((rashi, i) => ({ rashi, nakshatra: naks[Math.min(i, naks.length - 1)] }));
  }
  return out;
}

interface MilanResult {
  /** One representative case — always the first candidate on each side. */
  representative: AshtakootaResult;
  totalRange: KootaRange;
  ranges: Partial<Record<KootaId, KootaRange>>;
  combinations: number;
  /** Varna scored with the two charts swapped; the koota is directional. */
  varnaReversed: number;
}

function computeMilan(self: MoonCandidate[], partner: MoonCandidate[]): MilanResult | null {
  if (self.length === 0 || partner.length === 0) return null;

  const representative = computeAshtakoota(self[0], partner[0]);
  const reversed = computeAshtakoota(partner[0], self[0]);
  const varnaReversed = reversed.kootas.find((k) => k.koota === 'varna')?.points ?? 0;

  let totalMin = Infinity;
  let totalMax = -Infinity;
  const ranges: Partial<Record<KootaId, KootaRange>> = {};
  let combinations = 0;

  for (const a of self) {
    for (const b of partner) {
      const r = computeAshtakoota(a, b);
      combinations++;
      totalMin = Math.min(totalMin, r.total);
      totalMax = Math.max(totalMax, r.total);
      for (const k of r.kootas) {
        const cur = ranges[k.koota];
        ranges[k.koota] = cur
          ? { min: Math.min(cur.min, k.points), max: Math.max(cur.max, k.points) }
          : { min: k.points, max: k.points };
      }
    }
  }

  return {
    representative,
    totalRange: { min: totalMin, max: totalMax },
    ranges,
    combinations,
    varnaReversed,
  };
}

/**
 * Positions to hand to `computeSynastry`.
 *
 * With an unknown birth time the Moon can be anywhere in a ~13° arc, which is
 * wider than any orb this engine allows — a Moon aspect computed from the
 * window's midpoint would be a coin toss printed to one decimal place. It is
 * dropped instead, and the caveat says so.
 */
function synastryPositions(chart: ChartResult): PlanetPosition[] {
  const positions = chart.positions ?? chart.uncertainty?.midpointPositions ?? [];
  if (chart.meta.timeConfidence === 'unknown') {
    return positions.filter((p) => p.planet !== 'moon');
  }
  return positions;
}

const OVERLAY_POINTS: Planet[] = ['sun', 'moon', 'mercury', 'venus', 'mars', 'jupiter', 'saturn'];

/** Guest's planets dropped into the host's houses. Exact times only. */
function overlaysFor(host: ChartResult, guest: PlanetPosition[]): HouseOverlay[] {
  const cusps = host.western?.houses?.cusps;
  if (!cusps || cusps.length < 12) return [];
  const out: HouseOverlay[] = [];
  for (const planet of OVERLAY_POINTS) {
    const p = guest.find((x) => x.planet === planet);
    if (p) out.push({ planet, house: houseOf(p.lonTropical, cusps) });
  }
  return out;
}

const TENSE = new Set(['square', 'opposition', 'semi-square', 'sesquiquadrate']);
const EASY = new Set(['trine', 'sextile', 'conjunction']);
const PERSONAL = new Set<string>(['sun', 'moon', 'mercury', 'venus', 'mars']);

function personalTenor(aspects: AspectHit[]): { hard: number; soft: number } {
  let hard = 0;
  let soft = 0;
  for (const a of aspects) {
    if (!PERSONAL.has(a.a) || !PERSONAL.has(a.b)) continue;
    if (TENSE.has(a.type)) hard++;
    else if (EASY.has(a.type)) soft++;
  }
  return { hard, soft };
}

/**
 * Where the two readings actually diverge, stated deterministically. This is
 * the whole point of the screen, so it must not depend on an AI call being
 * affordable.
 */
function divergenceNote(total: number, aspects: AspectHit[]): string {
  const { hard, soft } = personalTenor(aspects);
  const passes = total >= 18;
  if (passes && hard > soft) {
    return 'These two disagree, and the disagreement is the interesting part. The guna count clears the conventional threshold — on the Moon-based arithmetic this is a suitable match. The Western contacts between your personal planets lean tense, which describes a pairing with a lot of friction in it. Read together: something that fits well on paper and is demanding to actually be inside of. That combination is common in long marriages and it is also common in exhausting ones; the arithmetic cannot tell you which.';
  }
  if (!passes && soft >= hard && soft > 0) {
    return 'These two disagree, and the disagreement is the interesting part. The guna count lands below the threshold traditional practice treats as a pass, while the Western contacts between your personal planets read as unusually easy. Read together: something that is comfortable to be inside of but that the Moon-based system would not endorse as a marriage. Guna Milan is answering a household question — nadi, gana, bhakoot are about constitution and lineage, not about whether you enjoy each other.';
  }
  if (passes) {
    return 'These two broadly agree. The guna count clears the conventional threshold and the Western contacts between your personal planets are not weighted towards friction. Agreement between two systems this different is worth noticing — and it is still not a verdict, because neither system was ever looking at whether you treat each other well.';
  }
  return 'These two broadly agree. The guna count sits below the conventional threshold and the Western contacts between your personal planets carry real friction. Worth saying plainly: that is a description of two charts, not a prediction about two people, and traditional practice itself lists cancellations and second opinions for exactly this situation.';
}

// ---------------------------------------------------------------------------
// Stored partner details (on-device only)
// ---------------------------------------------------------------------------

interface StoredPartner {
  name: string;
  input: BirthInput;
}

const BROAD_WINDOWS = {
  morning: { start: { hour: 5, minute: 0 }, end: { hour: 11, minute: 0 } },
  afternoon: { start: { hour: 11, minute: 0 }, end: { hour: 17, minute: 0 } },
  evening: { start: { hour: 17, minute: 0 }, end: { hour: 22, minute: 0 } },
  night: { start: { hour: 22, minute: 0 }, end: { hour: 5, minute: 0 } },
} as const;

const CONFIDENCE_WORD: Record<ChartResult['meta']['timeConfidence'], string> = {
  exact: 'exact birth time',
  approximate: 'approximate birth time',
  unknown: 'no birth time',
};

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export interface CompatibilityScreenProps {
  /** Set when this was opened from a connection's profile. */
  partnerUid?: string;
  /** Prefills the name field, so the reading reads like a conversation. */
  partnerName?: string;
  /** The server's deterministic pair score, when one exists for this pair. */
  pairScore?: { overall: number; scoreVersion: string } | null;
}

export function CompatibilityScreen({
  partnerUid,
  partnerName,
  pairScore,
}: CompatibilityScreenProps) {
  const app = useApp();
  const { t } = useI18n();
  const navigate = useNavigate();

  const storeKey = `compatPartner:${app.uid ?? 'anon'}:${partnerUid ?? 'manual'}`;

  const [stored, setStored] = useState<StoredPartner | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);

  // --- partner form state ---------------------------------------------------
  const [name, setName] = useState(partnerName ?? '');
  const [dob, setDob] = useState('');
  const [timeMode, setTimeMode] = useState<'exact' | 'approximate' | 'unknown'>('exact');
  const [exactTime, setExactTime] = useState('12:00');
  const [broad, setBroad] = useState<keyof typeof BROAD_WINDOWS | 'custom'>('morning');
  const [customStart, setCustomStart] = useState('06:00');
  const [customEnd, setCustomEnd] = useState('12:00');
  const [cityQuery, setCityQuery] = useState('');
  const [city, setCity] = useState<CityEntry | null>(null);
  const [manual, setManual] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLon, setManualLon] = useState('');
  const [manualTz, setManualTz] = useState('Asia/Kolkata');
  const [formError, setFormError] = useState('');

  const nameRef = useRef<HTMLLabelElement>(null);
  const dobRef = useRef<HTMLLabelElement>(null);
  const cityRef = useRef<HTMLLabelElement>(null);

  const cityResults = useMemo(() => searchCities(cityQuery), [cityQuery]);

  useEffect(() => {
    let live = true;
    localdb
      .get<StoredPartner>('kv', storeKey)
      .then((s) => {
        if (!live) return;
        if (s) setStored(s);
        setLoaded(true);
      })
      .catch(() => live && setLoaded(true));
    return () => {
      live = false;
    };
  }, [storeKey]);

  // --- computation ----------------------------------------------------------
  const selfChart = app.chart;

  const partnerChart = useMemo(() => {
    if (!stored) return null;
    try {
      return computeChart(stored.input, { ayanamsaId: app.ayanamsaId });
    } catch {
      return null;
    }
  }, [stored, app.ayanamsaId]);

  const reading = useMemo(() => {
    if (!selfChart || !partnerChart) return null;

    const milan = computeMilan(moonCandidates(selfChart), moonCandidates(partnerChart));

    const selfPositions = synastryPositions(selfChart);
    const partnerPositions = synastryPositions(partnerChart);
    // `score` is deliberately discarded: synastry has no total, and the one
    // computeSynastry returns exists for the matching engine, not for a reader.
    const { aspects } = computeSynastry(selfPositions, partnerPositions);

    const bothExact =
      selfChart.meta.timeConfidence === 'exact' && partnerChart.meta.timeConfidence === 'exact';
    const overlays = bothExact
      ? {
          partnerInSelf: overlaysFor(selfChart, partnerPositions),
          selfInPartner: overlaysFor(partnerChart, selfPositions),
        }
      : undefined;

    const selfSummary = deriveAstroSummary(selfChart);
    const partnerSummary = deriveAstroSummary(partnerChart);
    const doshas: DoshaFlags = {
      mangalSelf: selfSummary.mangalDosha,
      mangalPartner: partnerSummary.mangalDosha,
      nadiDosha: (milan?.representative.kootas.find((k) => k.koota === 'nadi')?.points ?? 1) === 0,
      bhakootDosha:
        (milan?.representative.kootas.find((k) => k.koota === 'bhakoot')?.points ?? 1) === 0,
    };

    const softTimes = [selfChart, partnerChart].filter(
      (c) => c.meta.timeConfidence !== 'exact',
    ).length;
    const caveat =
      softTimes === 0
        ? undefined
        : selfChart.meta.timeConfidence === 'unknown' ||
            partnerChart.meta.timeConfidence === 'unknown'
          ? 'A birth time here is unknown, so the Moon could be anywhere in a 13° arc — wider than any orb this engine allows. Moon contacts have been left out of the reading below rather than guessed at, and house overlays are unavailable. Everything shown comes from planets that barely move in a day.'
          : 'A birth time here is approximate. The slower planets below are solid, but the Moon can drift a few degrees across the window, so treat any Moon orb as soft. House overlays need an exact time and are not shown.';

    return { milan, aspects, overlays, doshas, caveat };
  }, [selfChart, partnerChart]);

  // --- optional AI comparison (never auto-fired) ---------------------------
  const [aiText, setAiText] = useState('');
  const [aiNote, setAiNote] = useState('');
  const [aiBusy, setAiBusy] = useState(false);

  const explainDifference = useCallback(async () => {
    if (!reading?.milan) {
      setAiNote('The guna table has to compute before it can be explained.');
      return;
    }
    if (app.allyBusy || aiBusy) {
      setAiNote('Astrally is still answering something else — one moment.');
      return;
    }
    setAiNote('');
    setAiBusy(true);
    try {
      const kootas = reading.milan.representative.kootas
        .map((k) => `${k.koota} ${k.points}/${k.max}`)
        .join(', ');
      const contacts = [...reading.aspects]
        .sort((a, b) => a.orb - b.orb)
        .slice(0, 14)
        .map((a) => `${a.a}-${a.type}-${a.b} (orb ${a.orb.toFixed(1)}°)`)
        .join('; ');
      const reply = await app.sendToAlly(
        [
          'Astrally’s engine produced these two results for one pair of charts. Do not recalculate anything and do not add placements I have not given you — explain and compare only.',
          `Guna Milan (Jyotish, sidereal, Moon-based): ${reading.milan.representative.total} of 36. Per koota: ${kootas}.`,
          `Doshas: nadi ${reading.doshas.nadiDosha ? 'present' : 'absent'}, bhakoot ${reading.doshas.bhakootDosha ? 'present' : 'absent'}, mangal — mine ${reading.doshas.mangalSelf}, theirs ${reading.doshas.mangalPartner}.`,
          `Western synastry contacts (tropical): ${contacts || 'none within orb'}.`,
          'In under 160 words, say where these two readings agree, where they disagree, and why two systems built on different zodiacs answering different questions can differ. No verdict on the relationship, no prediction of events.',
        ].join('\n'),
        'standard',
        { persist: false },
      );
      setAiText(reply.text);
    } catch (e) {
      setAiNote(String(e).slice(0, 180));
    } finally {
      setAiBusy(false);
    }
  }, [app, aiBusy, reading]);

  // --- form submit ----------------------------------------------------------
  const needsName = name.trim().length < 1;
  const needsDob = !dob;
  const needsCity = !manual && !city;

  const parseHM = (s: string) => {
    const [h, m] = s.split(':').map(Number);
    return { hour: h || 0, minute: m || 0 };
  };

  const buildTimeInput = (): BirthTimeInput => {
    if (timeMode === 'exact') return { kind: 'exact', time: parseHM(exactTime) };
    if (timeMode === 'unknown') return { kind: 'unknown' };
    if (broad === 'custom') {
      return {
        kind: 'approximate',
        window: { start: parseHM(customStart), end: parseHM(customEnd) },
        label: 'custom',
      };
    }
    return { kind: 'approximate', window: BROAD_WINDOWS[broad], label: broad };
  };

  /**
   * Never disabled. An unfinished form answers the tap by naming exactly what
   * is missing and scrolling to it — same contract as SocialOptInScreen.
   */
  const savePartner = async () => {
    if (needsName) {
      setFormError('Add a name, so the reading can tell the two of you apart.');
      nameRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (needsDob) {
      setFormError('Add their date of birth — the whole calculation rests on it.');
      dobRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      return;
    }
    // This check existed but was never enforced. Latitude, longitude and
    // timezone decide the lagna and every house cusp, so a missing birthplace
    // does not degrade the reading — it invalidates it.
    if (needsCity) {
      setFormError('Pick their birthplace from the list — it sets their Lagna and every bhava.');
      cityRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      return;
    }
    const place = manual
      ? {
          lat: Number(manualLat),
          lon: Number(manualLon),
          timezone: manualTz.trim(),
          label: 'Custom location',
        }
      : city
        ? {
            lat: city.lat,
            lon: city.lon,
            timezone: city.timezone,
            label: city.name,
            countryCode: city.countryCode,
          }
        : null;
    if (!place || Number.isNaN(place.lat) || Number.isNaN(place.lon) || !place.timezone) {
      setFormError(
        manual
          ? 'Latitude, longitude and an IANA timezone (like Asia/Kolkata) are all needed.'
          : 'Pick a birthplace from the list — typing the name is not the same as choosing it.',
      );
      cityRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      return;
    }
    const [y, m, d] = dob.split('-').map(Number);
    const next: StoredPartner = {
      name: name.trim(),
      input: { date: { year: y, month: m, day: d }, timeInput: buildTimeInput(), place },
    };
    setFormError('');
    setStored(next);
    setEditing(false);
    setAiText('');
    await localdb.set('kv', storeKey, next);
  };

  const clearPartner = async () => {
    if (!confirm('Delete these birth details from this device?')) return;
    setStored(null);
    setEditing(false);
    setAiText('');
    await localdb.del('kv', storeKey);
  };

  const startEditing = () => {
    if (stored) {
      setName(stored.name);
      const { date, timeInput, place } = stored.input;
      setDob(
        `${String(date.year).padStart(4, '0')}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`,
      );
      setTimeMode(timeInput.kind);
      if (timeInput.kind === 'exact') {
        setExactTime(
          `${String(timeInput.time.hour).padStart(2, '0')}:${String(timeInput.time.minute).padStart(2, '0')}`,
        );
      }
      setManual(true);
      setManualLat(String(place.lat));
      setManualLon(String(place.lon));
      setManualTz(place.timezone);
      setCityQuery(place.label ?? '');
    }
    setFormError('');
    setEditing(true);
  };

  const selfLabel = app.birthProfile?.displayName?.split(/\s+/)[0] || 'You';
  const theirLabel = stored?.name.split(/\s+/)[0] || partnerName?.split(/\s+/)[0] || 'Them';

  // -------------------------------------------------------------------------
  return (
    <div className="screen">
      <ScreenHeader
        title="Milan &amp; Synastry"
        subtitle={partnerName ? `You and ${partnerName}` : 'Compare two kundlis, two traditions'}
        back
      />

      {/* --- the framing panel: the reason this screen exists -------------- */}
      <div className="card col" style={{ gap: 10, marginBottom: 12 }}>
        <div className="h3">Two traditions, two different questions</div>
        <div className="small muted" style={{ lineHeight: 1.65 }}>
          Guna Milan and synastry are not two opinions about one thing. They are built on different
          zodiacs, measured from different points, and they were designed to answer different
          questions.
        </div>
        <div className="small muted" style={{ lineHeight: 1.65 }}>
          The <strong style={{ color: 'var(--vedic-ink)' }}>Jyotish</strong> side counts from{' '}
          <Term id="graha.chandra">Chandra</Term> — the Moon — in the sidereal zodiac and asks{' '}
          <em>is this match suitable?</em> It answers with a
          number out of 36, because it was built for families deciding whether a marriage should go
          ahead. <InfoDot term="kundali-milan" />
        </div>
        <div className="small muted" style={{ lineHeight: 1.65 }}>
          The <strong style={{ color: 'var(--western-ink)' }}>Western</strong> side measures the
          angles between every planet in both charts in the tropical zodiac and asks{' '}
          <em>what actually happens between these two people?</em> It answers with a description and
          no score at all, because it was built to describe a dynamic rather than approve one.{' '}
          <InfoDot term="synastry" />
        </div>
        <div className="small muted" style={{ lineHeight: 1.65 }}>
          So a high guna count sitting next to a difficult synastry is not a contradiction to be
          resolved. It is two instruments reading two different things, and where they disagree the
          disagreement is usually the most useful sentence on the page. Neither is a verdict on a
          person, and neither can tell you whether to be with someone. That part was never the
          software&rsquo;s to decide.
        </div>
      </div>

      {/* --- gate: we need the reader's own chart first -------------------- */}
      {!selfChart && (
        <div className="banner banner--danger small" role="status" style={{ marginBottom: 12 }}>
          Your own kundli has not been calculated yet, so there is nothing to compare against.
          <button
            className="btn btn--ghost btn--small btn--block"
            style={{ marginTop: 8 }}
            onClick={() => navigate('/charts')}
          >
            Open your kundli
          </button>
        </div>
      )}

      {/* --- partner details ---------------------------------------------- */}
      {selfChart && loaded && (!stored || editing) && (
        <PartnerForm
          {...{
            partnerUid,
            partnerName,
            pairScore,
            name,
            setName,
            nameRef,
            dob,
            setDob,
            dobRef,
            timeMode,
            setTimeMode,
            exactTime,
            setExactTime,
            broad,
            setBroad,
            customStart,
            setCustomStart,
            customEnd,
            setCustomEnd,
            cityQuery,
            setCityQuery,
            city,
            setCity,
            cityRef,
            cityResults,
            manual,
            setManual,
            manualLat,
            setManualLat,
            manualLon,
            setManualLon,
            manualTz,
            setManualTz,
            formError,
            setFormError,
            onSave: savePartner,
            onCancel: stored ? () => setEditing(false) : undefined,
          }}
        />
      )}

      {selfChart && stored && !editing && partnerChart && reading && (
        <>
          {/* --- the two answers, literally side by side ------------------ */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
              marginBottom: 12,
              alignItems: 'stretch',
            }}
          >
            <div
              data-mode="vedic"
              className="card col"
              style={{
                gap: 4,
                padding: 12,
                background: 'var(--mode-soft)',
                borderColor: 'var(--mode-line)',
              }}
            >
              <span className="mode-chip" style={{ alignSelf: 'flex-start' }}>
                Guna Milan
              </span>
              <div
                style={{
                  fontSize: '1.7rem',
                  fontWeight: 700,
                  color: 'var(--mode-ink)',
                  lineHeight: 1.1,
                  marginTop: 4,
                }}
              >
                {reading.milan ? `${reading.milan.representative.total}/36` : '—'}
              </div>
              <div className="tiny muted" style={{ lineHeight: 1.45 }}>
                Scores suitability for marriage, from the two Chandra positions.
              </div>
            </div>

            <div
              data-mode="western"
              className="card col"
              style={{
                gap: 4,
                padding: 12,
                background: 'var(--mode-soft)',
                borderColor: 'var(--mode-line)',
              }}
            >
              <span className="mode-chip" style={{ alignSelf: 'flex-start' }}>
                Synastry
              </span>
              <div
                style={{
                  fontSize: '1.7rem',
                  fontWeight: 700,
                  color: 'var(--mode-ink)',
                  lineHeight: 1.1,
                  marginTop: 4,
                }}
              >
                No score
              </div>
              <div className="tiny muted" style={{ lineHeight: 1.45 }}>
                Describes the psychological dynamic, from every planet in both charts.
              </div>
            </div>
          </div>

          {/* --- where they diverge, computed not generated ---------------- */}
          {reading.milan && (
            <div className="card col" style={{ gap: 8, marginBottom: 12 }}>
              <div className="section-title">Where the two readings land</div>
              <div className="small muted" style={{ lineHeight: 1.65 }}>
                {divergenceNote(reading.milan.representative.total, reading.aspects)}
              </div>
            </div>
          )}

          <div
            className="row"
            style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}
          >
            <div className="tiny faint">
              {selfLabel}: {CONFIDENCE_WORD[selfChart.meta.timeConfidence]} · {theirLabel}:{' '}
              {CONFIDENCE_WORD[partnerChart.meta.timeConfidence]}
            </div>
            <div className="row" style={{ gap: 6 }}>
              <button className="btn btn--ghost btn--small" onClick={startEditing}>
                Edit
              </button>
              <button className="btn btn--ghost btn--small danger" onClick={clearPartner}>
                Delete
              </button>
            </div>
          </div>

          {/* --- the two full readings, side by side where there is room --- */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 16,
              alignItems: 'start',
            }}
          >
            {reading.milan ? (
              <AshtakootaTable
                result={reading.milan.representative}
                doshas={reading.doshas}
                selfLabel={selfLabel}
                partnerLabel={theirLabel}
                totalRange={reading.milan.combinations > 1 ? reading.milan.totalRange : undefined}
                ranges={reading.milan.combinations > 1 ? reading.milan.ranges : undefined}
                varnaReversed={reading.milan.varnaReversed}
              />
            ) : (
              <section data-mode="vedic" className="col" style={{ gap: 10 }}>
                <span className="mode-chip" style={{ alignSelf: 'flex-start' }}>
                  Jyotish · sidereal
                </span>
                <div className="mode-rule" />
                <div className="banner banner--danger small">
                  Guna Milan is read entirely from <Term id="graha.chandra">Chandra</Term> — the
                  Moon — and one of these kundlis has no usable Chandra position. Without it there
                  is no table to show — a guessed position would change every one of the eight
                  scores.
                </div>
              </section>
            )}

            <SynastryView
              aspects={reading.aspects}
              selfLabel={selfLabel}
              partnerLabel={theirLabel}
              overlays={reading.overlays}
              caveat={reading.caveat}
            />
          </div>

          {/* --- optional AI comparison ------------------------------------ */}
          <div className="card col" style={{ gap: 8, marginTop: 14 }}>
            <div className="section-title">Ask Astrally to compare them</div>
            <div className="tiny muted" style={{ lineHeight: 1.6 }}>
              Everything above is already computed and free. This hands those exact numbers to
              Astrally and asks it to talk through the difference in its own words. It spends one of
              your messages, and it never recalculates anything.
            </div>
            {aiNote && (
              <div className="banner banner--danger small" role="status">
                {aiNote}
              </div>
            )}
            <button
              className={`btn ${aiText ? 'btn--ghost' : 'btn--primary'} btn--block`}
              onClick={explainDifference}
            >
              {aiBusy ? t('common.loading') : aiText ? 'Ask again' : 'Explain the difference · 1 message'}
            </button>
            {aiText && (
              <div className="small muted fade-in" style={{ lineHeight: 1.65, marginTop: 4 }}>
                {aiText}
              </div>
            )}
          </div>

          <div className="tiny faint" style={{ marginTop: 14, lineHeight: 1.6 }}>
            Both readings are computed on this device from the birth details you entered. Astrally
            never uploads another person&rsquo;s birth data. Astrology is one way of reading people —
            these are two traditions&rsquo; descriptions of two charts, not evidence about two
            people and not a forecast.
          </div>
        </>
      )}

      {selfChart && stored && !editing && !partnerChart && (
        <div className="banner banner--danger small" role="status">
          Those birth details could not be turned into a kundli. Check the date, the timezone and
          the coordinates.
          <button
            className="btn btn--ghost btn--small btn--block"
            style={{ marginTop: 8 }}
            onClick={startEditing}
          >
            Edit the details
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Partner birth-details form
// ---------------------------------------------------------------------------

interface PartnerFormProps {
  partnerUid?: string;
  partnerName?: string;
  pairScore?: { overall: number; scoreVersion: string } | null;
  name: string;
  setName: (v: string) => void;
  nameRef: React.RefObject<HTMLLabelElement>;
  dob: string;
  setDob: (v: string) => void;
  dobRef: React.RefObject<HTMLLabelElement>;
  timeMode: 'exact' | 'approximate' | 'unknown';
  setTimeMode: (v: 'exact' | 'approximate' | 'unknown') => void;
  exactTime: string;
  setExactTime: (v: string) => void;
  broad: keyof typeof BROAD_WINDOWS | 'custom';
  setBroad: (v: keyof typeof BROAD_WINDOWS | 'custom') => void;
  customStart: string;
  setCustomStart: (v: string) => void;
  customEnd: string;
  setCustomEnd: (v: string) => void;
  cityQuery: string;
  setCityQuery: (v: string) => void;
  city: CityEntry | null;
  setCity: (v: CityEntry | null) => void;
  cityRef: React.RefObject<HTMLLabelElement>;
  cityResults: CityEntry[];
  manual: boolean;
  setManual: (v: boolean) => void;
  manualLat: string;
  setManualLat: (v: string) => void;
  manualLon: string;
  setManualLon: (v: string) => void;
  manualTz: string;
  setManualTz: (v: string) => void;
  formError: string;
  setFormError: (v: string) => void;
  onSave: () => void;
  onCancel?: () => void;
}

function PartnerForm(p: PartnerFormProps) {
  const incomplete = p.name.trim().length < 1 || !p.dob || (!p.manual && !p.city);

  return (
    <div className="col" style={{ gap: 12 }}>
      {p.partnerUid && (
        <div className="banner banner--info small">
          Astrally does not hold {p.partnerName ?? 'their'} birth details, and would not hand them
          over if it did — the cloud only ever stores sign-level summaries, and never for someone
          else to read. To run the real 36-guna table and a real synastry, ask them for their birth
          date, time and place and enter it here.
          {p.pairScore && (
            <>
              {' '}
              In the meantime the deterministic pair score Astrally already computed for the two of
              you is <strong>{p.pairScore.overall}</strong> — a profile-and-signs estimate, not
              guna milan.
            </>
          )}
        </div>
      )}

      <div className="card col" style={{ gap: 12 }}>
        <div className="h3">{p.partnerName ? `${p.partnerName}’s birth details` : 'Their birth details'}</div>
        <div className="tiny faint" style={{ lineHeight: 1.55 }}>
          Stored on this device only, and deletable in one tap once the reading is up. Do not enter
          someone&rsquo;s details without asking them first.
        </div>

        <label className="field" ref={p.nameRef}>
          Name
          <input
            className="input"
            value={p.name}
            placeholder="Their name"
            maxLength={40}
            onChange={(e) => {
              p.setFormError('');
              p.setName(e.target.value);
            }}
          />
        </label>

        <label className="field" ref={p.dobRef}>
          Date of birth
          <input
            className="input"
            type="date"
            value={p.dob}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => {
              p.setFormError('');
              p.setDob(e.target.value);
            }}
          />
        </label>

        <div className="field">
          Birth time
          <div className="seg">
            {(
              [
                ['exact', 'Exact'],
                ['approximate', 'Roughly'],
                ['unknown', 'Unknown'],
              ] as const
            ).map(([mode, lbl]) => (
              <button
                key={mode}
                className={`seg__opt ${p.timeMode === mode ? 'seg__opt--on' : ''}`}
                onClick={() => p.setTimeMode(mode)}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>

        {p.timeMode === 'exact' && (
          <input
            className="input"
            type="time"
            value={p.exactTime}
            onChange={(e) => p.setExactTime(e.target.value)}
          />
        )}
        {p.timeMode === 'approximate' && (
          <div className="col" style={{ gap: 8 }}>
            <select
              className="select"
              value={p.broad}
              onChange={(e) => p.setBroad(e.target.value as keyof typeof BROAD_WINDOWS | 'custom')}
            >
              <option value="morning">Morning (5am–11am)</option>
              <option value="afternoon">Afternoon (11am–5pm)</option>
              <option value="evening">Evening (5pm–10pm)</option>
              <option value="night">Night (10pm–5am)</option>
              <option value="custom">A window I know</option>
            </select>
            {p.broad === 'custom' && (
              <div className="row">
                <input
                  className="input"
                  type="time"
                  value={p.customStart}
                  onChange={(e) => p.setCustomStart(e.target.value)}
                />
                <span className="muted">→</span>
                <input
                  className="input"
                  type="time"
                  value={p.customEnd}
                  onChange={(e) => p.setCustomEnd(e.target.value)}
                />
              </div>
            )}
          </div>
        )}
        {p.timeMode !== 'exact' && (
          <div className="tiny faint" style={{ lineHeight: 1.55 }}>
            Guna Milan is read entirely from Chandra — the Moon — and it changes nakshatra roughly
            every day, so without an exact time the table below will show a range rather than one
            number. That is the honest version, not a degraded one. <InfoDot term="birth-time" />
          </div>
        )}

        {!p.manual ? (
          <>
            <label className="field" ref={p.cityRef}>
              Birthplace
              <input
                className="input"
                placeholder="Search a city"
                value={p.cityQuery}
                onChange={(e) => {
                  p.setFormError('');
                  p.setCityQuery(e.target.value);
                  // Typing again invalidates an earlier pick — otherwise the
                  // field can read "Delhi" while the stored place is Mumbai.
                  p.setCity(null);
                }}
              />
            </label>
            <div className="col" style={{ gap: 6 }}>
              {p.cityResults.map((c) => (
                <button
                  key={`${c.name}-${c.countryCode}`}
                  className={`btn btn--ghost ${p.city?.name === c.name ? 'chip--on' : ''}`}
                  style={{ justifyContent: 'space-between' }}
                  onClick={() => {
                    p.setFormError('');
                    p.setCity(c);
                    p.setCityQuery(`${c.name}, ${c.country}`);
                  }}
                >
                  <span>
                    {c.name}
                    {c.admin ? `, ${c.admin}` : ''}
                  </span>
                  <span className="faint small">{c.country}</span>
                </button>
              ))}
            </div>
            <button className="btn btn--ghost btn--small" onClick={() => p.setManual(true)}>
              Enter coordinates instead
            </button>
          </>
        ) : (
          <div className="col" style={{ gap: 8 }}>
            <label className="field">
              Latitude
              <input
                className="input"
                value={p.manualLat}
                placeholder="19.0760"
                onChange={(e) => p.setManualLat(e.target.value)}
              />
            </label>
            <label className="field">
              Longitude
              <input
                className="input"
                value={p.manualLon}
                placeholder="72.8777"
                onChange={(e) => p.setManualLon(e.target.value)}
              />
            </label>
            <label className="field">
              IANA timezone
              <input
                className="input"
                value={p.manualTz}
                placeholder="Asia/Kolkata"
                onChange={(e) => p.setManualTz(e.target.value)}
              />
            </label>
            <button className="btn btn--ghost btn--small" onClick={() => p.setManual(false)}>
              ← Search cities instead
            </button>
          </div>
        )}

        {p.formError && (
          <div className="banner banner--danger small" role="status">
            {p.formError}
          </div>
        )}

        <div className="row">
          {p.onCancel && (
            <button className="btn btn--ghost" onClick={p.onCancel}>
              Cancel
            </button>
          )}
          <button
            className={`btn btn--primary ${incomplete ? 'btn--incomplete' : ''}`}
            style={{ flex: 1 }}
            onClick={p.onSave}
          >
            Compare the kundlis
          </button>
        </div>
      </div>
    </div>
  );
}
